import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Mail, History, Send, MapPin, Tag, Building2, UploadCloud, Users, AlertCircle, Calendar, Pencil, Save, Clock, Download, Archive, Eye } from "lucide-react";
import { addAlert, updateAlert } from "@/data/alertsStore";
import { AlertStatus } from "@/data/mockAlerts";
import { useAlerts } from "@/hooks/useAlerts";
import { addEmail, updateEmail } from "@/data/emailsStore";
import { useEmails } from "@/hooks/useEmails";
import { useToast } from "@/hooks/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const THEMES = ['Politique', 'Économie', 'Santé', 'Éducation', 'Environnement', 'Infrastructure'] as const;
const REGIONS = [
  "Tanger-Tétouan-Al Hoceïma", "L'Oriental", "Fès-Meknès", "Rabat-Salé-Kénitra",
  "Béni Mellal-Khénifra", "Casablanca-Settat", "Marrakech-Safi", "Drâa-Tafilalet",
  "Souss-Massa", "Guelmim-Oued Noun", "Laâyoune-Sakia El Hamra", "Dakhla-Oued Ed-Dahab",
  "Toutes"
];

const AdminAlertsPage = () => {
  const { toast } = useToast();
  const { alerts } = useAlerts();
  
  // State for new in-app alert
  const [inAppTitle, setInAppTitle] = useState("");
  const [inAppDesc, setInAppDesc] = useState("");
  const [inAppRegion, setInAppRegion] = useState("Toutes");
  const [inAppTheme, setInAppTheme] = useState<any>("Politique");
  const [inAppImportant, setInAppImportant] = useState(false);
  const [inAppImageUrl, setInAppImageUrl] = useState("");
  const [inAppStatus, setInAppStatus] = useState<AlertStatus>("publie");
  const [inAppScheduledFor, setInAppScheduledFor] = useState("");
  
  const [activeTab, setActiveTab] = useState("inapp");
  const [editingAlertId, setEditingAlertId] = useState<string | null>(null);

  const handleEditAlert = (alert: any) => {
    setEditingAlertId(alert.id);
    setInAppTitle(alert.title.fr || "");
    setInAppDesc(alert.description.fr || "");
    setInAppRegion(alert.region || "Toutes");
    setInAppTheme(alert.theme || "Politique");
    setInAppImportant(alert.isImportant || false);
    setInAppImageUrl(alert.imageUrl || "");
    setInAppScheduledFor(alert.scheduledFor || "");
    setActiveTab("inapp");
  };

  const handleSaveAction = (status: AlertStatus) => {
    if (!inAppTitle || !inAppDesc) {
      toast({ title: "Erreur", description: "Le titre et la description sont requis", variant: "destructive" });
      return;
    }
    
    // Dérivation du statut si l'utilisateur a rempli une date
    const finalStatus = inAppScheduledFor && status !== 'brouillon' ? 'programme' : status;

    const payload = {
      title: { fr: inAppTitle },
      description: { fr: inAppDesc },
      sourceName: "Administration Kayn'in",
      sourceType: "Institution" as any,
      region: inAppRegion,
      theme: inAppTheme,
      isImportant: inAppImportant,
      imageUrl: inAppImageUrl || undefined,
      status: finalStatus,
      isActive: true,
      scheduledFor: finalStatus === 'programme' ? inAppScheduledFor : undefined
    };

    if (editingAlertId) {
      updateAlert(editingAlertId, payload);
      toast({ title: "Modifications enregistrées", description: "L'alerte a été mise à jour avec succès." });
    } else {
      addAlert(payload);
      toast({ title: "Alerte sauvegardée", description: `L'alerte a été sauvegardée en statut: ${finalStatus}.` });
    }
    
    // Reset
    resetInAppForm();
  };

  const resetInAppForm = () => {
    setEditingAlertId(null);
    setInAppTitle("");
    setInAppDesc("");
    setInAppImportant(false);
    setInAppImageUrl("");
    setInAppScheduledFor("");
  };
  // State for Email Campaign
  const { emails } = useEmails();
  const [emailCategory, setEmailCategory] = useState("tous");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSegment, setEmailSegment] = useState("tous");
  const [emailAttachmentUrl, setEmailAttachmentUrl] = useState("");
  const [emailScheduledFor, setEmailScheduledFor] = useState("");
  const [editingEmailId, setEditingEmailId] = useState<string | null>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
       const reader = new FileReader();
       reader.onloadend = () => {
         setInAppImageUrl(reader.result as string);
       };
       reader.readAsDataURL(file);
    }
  };

  const handleEmailAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
       const reader = new FileReader();
       reader.onloadend = () => {
         setEmailAttachmentUrl(reader.result as string);
       };
       reader.readAsDataURL(file);
    }
  };

  const handleSaveEmailAction = (status: AlertStatus) => {
    if (!emailSubject || !emailBody) {
      toast({ title: "Erreur", description: "L'objet et le corps du mail sont requis", variant: "destructive" });
      return;
    }
    
    const finalStatus = emailScheduledFor && status !== 'brouillon' ? 'programme' : status;

    const payload = {
      category: emailCategory,
      segment: emailSegment,
      subject: emailSubject,
      body: emailBody,
      attachmentDataUrl: emailAttachmentUrl || undefined,
      status: finalStatus,
      scheduledFor: finalStatus === 'programme' ? emailScheduledFor : undefined
    };

    if (editingEmailId) {
      updateEmail(editingEmailId, payload);
      toast({ title: "Modifications enregistrées", description: "L'email a été mis à jour avec succès." });
    } else {
      addEmail(payload);
      toast({ title: "Email sauvegardé", description: `Enregistré avec le statut: ${finalStatus}.` });
    }
    
    resetEmailForm();
  };

  const resetEmailForm = () => {
    setEditingEmailId(null);
    setEmailCategory("tous");
    setEmailSubject("");
    setEmailBody("");
    setEmailSegment("tous");
    setEmailAttachmentUrl("");
    setEmailScheduledFor("");
  };

  const handleEditEmail = (email: any) => {
    setEditingEmailId(email.id);
    setEmailCategory(email.category || "tous");
    setEmailSegment(email.segment || "tous");
    setEmailSubject(email.subject || "");
    setEmailBody(email.body || "");
    setEmailAttachmentUrl(email.attachmentDataUrl || "");
    setEmailScheduledFor(email.scheduledFor || "");
  };

  const [emailHistoryFilter, setEmailHistoryFilter] = useState("tous");
  const filteredEmails = emails.filter(e => emailHistoryFilter === "tous" || e.category === emailHistoryFilter);

  const exportEmails = () => {
    let csv = "ID,Categorie,Segment,Sujet,Statut,Date,Vues (Simulé)\n";
    filteredEmails.forEach(e => {
      csv += `${e.id},${e.category},${e.segment},"${e.subject}",${e.status},${e.scheduledFor || "Immédiat"},${Math.floor(Math.random() * 500)}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", "historique_emails.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [alertHistoryFilterTheme, setAlertHistoryFilterTheme] = useState("Toutes");
  const filteredAlerts = alerts.filter(a => alertHistoryFilterTheme === "Toutes" || a.theme === alertHistoryFilterTheme);

  const exportAlerts = () => {
    let csv = "ID,Theme,Region,Titre,Statut,Important,Date,Vues (Simulé)\n";
    filteredAlerts.forEach(a => {
      csv += `${a.id},${a.theme},${a.region},"${a.title?.fr || ""}",${a.status},${a.isImportant ? "Oui" : "Non"},${a.date},${Math.floor(Math.random() * 2000)}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", "historique_alertes_inapp.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 max-w-5xl mx-auto pb-12">
      <div>
        <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-3">
          <Bell className="w-8 h-8 text-primary" />
          Communication & Alertes
        </h1>
        <p className="text-muted-foreground font-body mt-2">
          Gérez l'envoi de notifications push in-app et les campagnes d'emailing ciblées pour les citoyens.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-8 h-auto p-1 bg-muted rounded-2xl">
          <TabsTrigger value="inapp" className="py-3 rounded-xl font-body font-semibold data-[state=active]:shadow-sm">
            <Bell className="w-4 h-4 mr-2" /> Alertes In-App
          </TabsTrigger>
          <TabsTrigger value="email" className="py-3 rounded-xl font-body font-semibold data-[state=active]:shadow-sm">
            <Mail className="w-4 h-4 mr-2" /> Campagne Email
          </TabsTrigger>
          <TabsTrigger value="history" className="py-3 rounded-xl font-body font-semibold data-[state=active]:shadow-sm">
            <History className="w-4 h-4 mr-2" /> Historique Public
          </TabsTrigger>
        </TabsList>
        
        {/* --- TAB IN-APP ALERT --- */}
        <TabsContent value="inapp">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card border border-border p-6 rounded-2xl shadow-sm space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                    <Bell className="w-5 h-5 text-primary" /> 
                    {editingAlertId ? "Modifier l'alerte" : "Rédiger une nouvelle alerte"}
                  </h2>
                  {editingAlertId && (
                    <Button variant="ghost" size="sm" onClick={resetInAppForm} className="text-muted-foreground">
                      Annuler la modification
                    </Button>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold mb-2 block">Titre de l'alerte</label>
                    <input 
                      type="text" 
                      value={inAppTitle}
                      onChange={(e) => setInAppTitle(e.target.value)}
                      placeholder="Ex: Nouvelle conférence sur la santé..."
                      className="w-full h-11 px-4 rounded-xl border border-input bg-background"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-semibold mb-2 block">Message court</label>
                    <Textarea 
                      value={inAppDesc}
                      onChange={(e) => setInAppDesc(e.target.value)}
                      placeholder="Décrivez brièvement l'alerte (max 200 caractères)..."
                      className="w-full min-h-[100px] border-input rounded-xl"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold mb-2 block flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Région cible</label>
                      <Select value={inAppRegion} onValueChange={setInAppRegion}>
                        <SelectTrigger className="w-full h-11 bg-background border-input rounded-xl">
                          <SelectValue placeholder="Région" />
                        </SelectTrigger>
                        <SelectContent>
                          {REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-semibold mb-2 block flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Thématique</label>
                      <Select value={inAppTheme} onValueChange={setInAppTheme}>
                        <SelectTrigger className="w-full h-11 bg-background border-input rounded-xl">
                          <SelectValue placeholder="Thème" />
                        </SelectTrigger>
                        <SelectContent>
                          {THEMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-semibold mb-2 block flex items-center gap-1"><UploadCloud className="w-3.5 h-3.5" /> Image associée</label>
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 bg-background"
                    />
                  </div>

                  <div className="pt-2">
                      <div>
                        <label className="text-sm font-semibold mb-2 block flex items-center gap-1"><Clock className="w-3.5 h-3.5"/> Date programmée (Optionnel pour publier plus tard)</label>
                        <input 
                          type="datetime-local" 
                          value={inAppScheduledFor}
                          onChange={e => setInAppScheduledFor(e.target.value)}
                          className="w-full h-11 px-4 rounded-xl border border-input bg-background"
                        />
                      </div>
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <input 
                      type="checkbox" 
                      id="important-alert"
                      checked={inAppImportant}
                      onChange={(e) => setInAppImportant(e.target.checked)}
                      className="w-4 h-4 accent-destructive"
                    />
                    <label htmlFor="important-alert" className="text-sm font-semibold text-destructive cursor-pointer">
                      Marquer cette alerte comme URGENTE / IMPORTANTE
                    </label>
                  </div>
                </div>

                <div className="pt-4 border-t border-border flex flex-wrap gap-2 justify-end">
                  <Button variant="outline" onClick={() => handleSaveAction('brouillon')} className="rounded-full px-6 py-5 font-bold shadow-sm gap-2">
                    <Save className="w-4 h-4" /> Enregistrer un Brouillon
                  </Button>
                  {inAppScheduledFor ? (
                    <Button onClick={() => handleSaveAction('programme')} className="rounded-full px-6 py-5 font-bold shadow-md bg-blue-600 hover:bg-blue-700 text-white gap-2">
                      <Clock className="w-4 h-4" /> Programmer
                    </Button>
                  ) : (
                    <Button onClick={() => handleSaveAction('publie')} className="rounded-full px-6 py-5 font-bold shadow-md hover:shadow-lg gap-2">
                      <Send className="w-4 h-4" /> Publier Immédiatement
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Preview Card */}
            <div>
              <div className="bg-muted/30 border border-border border-dashed p-6 rounded-2xl relative">
                <span className="absolute -top-3 left-6 px-2 py-0.5 bg-background text-xs font-bold text-muted-foreground border border-border rounded-full">
                  Aperçu Public
                </span>
                
                <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden pointer-events-none mt-2">
                  {inAppImageUrl && (
                    <div className="w-full h-32 bg-muted relative overflow-hidden">
                      <img src={inAppImageUrl} className="w-full h-full object-cover" alt="preview" />
                      <Badge className="absolute bottom-2 left-2 bg-background/90 text-foreground border-none text-[10px]">
                        {inAppTheme}
                      </Badge>
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {!inAppImageUrl && (
                        <Badge variant="secondary" className="text-[9px] bg-muted">{inAppTheme}</Badge>
                      )}
                      <span className="text-[10px] font-semibold text-primary/80">Il y a un instant</span>
                      {inAppRegion !== "Toutes" && (
                        <Badge variant="outline" className="text-[9px] ml-auto border-primary/30 text-primary">
                          {inAppRegion}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-base font-display font-bold text-foreground leading-tight mb-1.5">
                      {inAppTitle || "Titre de l'alerte"}
                    </h3>
                    <p className="text-xs font-body text-muted-foreground line-clamp-3">
                      {inAppDesc || "La description s'affichera ici..."}
                    </p>
                    <div className="flex gap-2 mt-3">
                      {inAppImportant && (
                        <Badge className="bg-destructive/10 text-destructive text-[9px] uppercase hover:bg-destructive/10">Important</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* --- TAB EMAIL CAMPAIGN --- */}
        <TabsContent value="email">
           <div className="max-w-3xl mx-auto space-y-6">
             <div className="bg-card border border-border p-6 rounded-2xl shadow-sm space-y-6">
                 <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" /> Rédiger une Newsletter / Email
                </h2>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                      <label className="text-sm font-semibold mb-2 block flex items-center gap-1"><Tag className="w-4 h-4" /> Catégorie cible</label>
                      <Select value={emailCategory} onValueChange={setEmailCategory}>
                        <SelectTrigger className="w-full h-11 bg-background border-input rounded-xl">
                          <SelectValue placeholder="Catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tous">Toutes les catégories</SelectItem>
                          <SelectItem value="citoyens">Citoyens</SelectItem>
                          <SelectItem value="partis">Partis Politiques</SelectItem>
                          <SelectItem value="ong">ONG & Associations</SelectItem>
                          <SelectItem value="institutions">Institutions Publiques</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-semibold mb-2 block flex items-center gap-1"><Users className="w-4 h-4" /> Segment spécifique</label>
                      <Select value={emailSegment} onValueChange={setEmailSegment}>
                        <SelectTrigger className="w-full h-11 bg-background border-input rounded-xl">
                          <SelectValue placeholder="Tous..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tous">Tous</SelectItem>
                          <SelectItem value="hommes">Hommes (H)</SelectItem>
                          <SelectItem value="femmes">Femmes (F)</SelectItem>
                          {REGIONS.map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-semibold mb-2 block">Objet de l'email</label>
                    <input 
                      type="text" 
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Ex: Participez au grand sondage national..."
                      className="w-full h-11 px-4 rounded-xl border border-input bg-background"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-semibold mb-2 block">Corps de l'email (Format riche simulé)</label>
                    <Textarea 
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Cher(e) citoyen(ne), ..."
                      className="w-full min-h-[250px] border-input rounded-xl font-body"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold mb-2 block flex items-center gap-1"><UploadCloud className="w-3.5 h-3.5" /> Joindre un fichier (Optionnel)</label>
                    <input 
                      type="file" 
                      onChange={handleEmailAttachmentUpload}
                      className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 bg-background"
                    />
                  </div>

                  <div className="pt-2">
                      <div>
                        <label className="text-sm font-semibold mb-2 block flex items-center gap-1"><Clock className="w-3.5 h-3.5"/> Programmer l'envoi</label>
                        <input 
                          type="datetime-local" 
                          value={emailScheduledFor}
                          onChange={e => setEmailScheduledFor(e.target.value)}
                          className="w-full h-11 px-4 rounded-xl border border-input bg-background"
                        />
                      </div>
                  </div>
                </div>
                
                <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <AlertCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-sm font-body text-blue-800">
                    <strong>Simulation:</strong> L'envoi est stocké sans faire partir de vrais emails. Vous pouvez retrouver ces campagnes dans l'historique ci-dessous.
                  </div>
                </div>

                <div className="pt-4 border-t border-border flex flex-wrap gap-2 justify-end">
                  <Button variant="outline" onClick={() => handleSaveEmailAction('brouillon')} className="rounded-full px-6 py-5 font-bold shadow-sm gap-2">
                    <Save className="w-4 h-4" /> Sauvegarder (Brouillon)
                  </Button>
                  {emailScheduledFor ? (
                    <Button onClick={() => handleSaveEmailAction('programme')} className="rounded-full px-6 py-5 font-bold shadow-md bg-blue-600 hover:bg-blue-700 text-white gap-2">
                      <Clock className="w-4 h-4" /> Programmer l'envoi
                    </Button>
                  ) : (
                    <Button onClick={() => handleSaveEmailAction('publie')} className="rounded-full px-6 py-5 font-bold shadow-md bg-blue-600 hover:bg-blue-700 text-white gap-2">
                      <Send className="w-4 h-4" /> Envoyer la campagne
                    </Button>
                  )}
                </div>
             </div>

             {/* Historique des Emails */}
             <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden mt-8">
               <div className="p-4 border-b border-border bg-muted/20 flex flex-col md:flex-row md:items-center justify-between gap-4">
                 <h3 className="font-display font-semibold flex items-center gap-2">
                   <History className="w-5 h-5" /> Historique des Campagnes Email
                 </h3>
                 <div className="flex gap-2 items-center flex-wrap">
                   <Select value={emailHistoryFilter} onValueChange={setEmailHistoryFilter}>
                     <SelectTrigger className="w-[160px] h-8 text-xs bg-white"><SelectValue placeholder="Catégorie..." /></SelectTrigger>
                     <SelectContent>
                       <SelectItem value="tous">Toutes les cibles</SelectItem>
                       <SelectItem value="citoyens">Citoyens</SelectItem>
                       <SelectItem value="partis">Partis</SelectItem>
                       <SelectItem value="ong">ONG</SelectItem>
                       <SelectItem value="institutions">Institutions</SelectItem>
                     </SelectContent>
                   </Select>
                   <Button variant="outline" size="sm" className="h-8 text-xs gap-1 bg-white hover:bg-muted" onClick={() => toast({title: "Archivage réussi", description: "Les emails du mois précédent ont été archivés."})}>
                     <Archive className="w-3 h-3 text-muted-foreground" /> Archiver
                   </Button>
                   <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-blue-600 border-blue-200 hover:bg-blue-100 bg-white" onClick={exportEmails}>
                     <Download className="w-3 h-3" /> Exporter CSV
                   </Button>
                 </div>
               </div>
               
               {filteredEmails.length === 0 ? (
                 <div className="p-10 text-center text-muted-foreground">Aucun email dans l'historique.</div>
               ) : (
                  <div className="divide-y divide-border">
                    {filteredEmails.map(e => {
                      const vues = Math.floor(Math.random() * 500) + 50; // Simulation
                      return (
                      <div key={e.id} className="p-4 hover:bg-muted/30 transition-colors flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
                         <div>
                           <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge variant="secondary" className="text-[10px] uppercase font-bold">{e.category}</Badge>
                              <Badge variant="outline" className="text-[10px] text-muted-foreground">{e.segment}</Badge>
                              <Badge variant="outline" className={`text-[10px] h-4 leading-none py-0 ${
                                e.status === 'brouillon' ? 'text-amber-500 border-amber-500' :
                                e.status === 'programme' ? 'text-blue-500 border-blue-500' :
                                'text-emerald-500 border-emerald-500'
                              }`}>
                                {e.status === 'brouillon' ? 'Brouillon' : e.status === 'programme' ? `Prévu: ${e.scheduledFor?.replace('T',' ')}` : `Envoyé le ${e.date || "récemment"}`}
                              </Badge>
                              {e.status !== 'brouillon' && (
                                <span className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1 ml-2">
                                  <Eye className="w-3 h-3" /> {vues} Ouvertures
                                </span>
                              )}
                           </div>
                           <h4 className="font-bold text-sm text-foreground">{e.subject}</h4>
                           <p className="text-xs text-muted-foreground line-clamp-1 max-w-xl">{e.body}</p>
                         </div>
                         <div className="flex items-center gap-2 shrink-0">
                            {e.attachmentDataUrl && <Badge variant="secondary" className="bg-blue-50 text-blue-600"><UploadCloud className="w-3 h-3 mr-1"/> PJ</Badge>}
                            <Button variant="outline" size="sm" className="h-8 gap-1 border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground" onClick={() => handleEditEmail(e)}>
                               <Pencil className="w-3.5 h-3.5" /> Modifier
                            </Button>
                         </div>
                       </div>
                    );
                  })}
                  </div>
               )}
             </div>
           </div>
        </TabsContent>

        {/* --- TAB HISTORIQUE --- */}
        <TabsContent value="history">
           <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
             <div className="p-4 border-b border-border bg-muted/20 flex flex-col md:flex-row md:items-center justify-between gap-4">
               <h3 className="font-display font-semibold flex items-center gap-2">
                 <History className="w-5 h-5" /> Alertes In-App récemment envoyées
               </h3>
               <div className="flex gap-2 items-center flex-wrap">
                 <Select value={alertHistoryFilterTheme} onValueChange={setAlertHistoryFilterTheme}>
                   <SelectTrigger className="w-[160px] h-8 text-xs bg-white"><SelectValue placeholder="Thème..." /></SelectTrigger>
                   <SelectContent>
                     <SelectItem value="Toutes">Tous les thèmes</SelectItem>
                     {THEMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                   </SelectContent>
                 </Select>
                 <Button variant="outline" size="sm" className="h-8 text-xs gap-1 bg-white hover:bg-muted" onClick={() => toast({title: "Archivage réussi", description: "Les alertes in-app du mois précédent ont été archivées."})}>
                   <Archive className="w-3 h-3 text-muted-foreground" /> Archiver
                 </Button>
                 <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-blue-600 border-blue-200 hover:bg-blue-100 bg-white" onClick={exportAlerts}>
                   <Download className="w-3 h-3" /> Exporter CSV
                 </Button>
               </div>
             </div>
             
             {filteredAlerts.length === 0 ? (
               <div className="p-10 text-center text-muted-foreground">Aucune alerte dans l'historique.</div>
             ) : (
                <div className="divide-y divide-border">
                  {filteredAlerts.map(a => {
                    const vues = Math.floor(Math.random() * 2000) + 100;
                    return (
                    <div key={a.id} className="p-4 hover:bg-muted/30 transition-colors flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
                       <div>
                         <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="secondary" className="text-[10px]">{a.theme}</Badge>
                            <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="w-3 h-3"/> {a.date}</span>
                            {a.isImportant && <Badge className="bg-destructive text-destructive-foreground text-[10px] h-4 leading-none py-0">Urgent</Badge>}
                            <Badge variant="outline" className={`text-[10px] h-4 leading-none py-0 ${
                              a.status === 'brouillon' ? 'text-amber-500 border-amber-500' :
                              a.status === 'programme' ? 'text-blue-500 border-blue-500' :
                              'text-emerald-500 border-emerald-500'
                            }`}>
                              {a.status === 'brouillon' ? 'Brouillon' : a.status === 'programme' ? `Prévu: ${a.scheduledFor?.replace('T',' ')}` : 'Publié'}
                            </Badge>
                            {a.status !== 'brouillon' && (
                              <span className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1 ml-2">
                                <Eye className="w-3 h-3" /> {vues} Vues
                              </span>
                            )}
                         </div>
                         <h4 className={`font-bold text-sm ${a.isActive === false ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{a.title.fr}</h4>
                         <p className="text-xs text-muted-foreground line-clamp-1 max-w-xl">{a.description.fr}</p>
                       </div>
                       <div className="flex items-center gap-4 shrink-0 mt-2 md:mt-0">
                          <span className="text-xs px-2 py-1 bg-muted rounded-md text-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {a.region}
                          </span>
                          <div className="flex items-center gap-2 border-l border-border px-4">
                            <span className="text-[10px] font-semibold text-muted-foreground">{a.isActive !== false ? 'Actif' : 'Inactif'}</span>
                            <Switch 
                              checked={a.isActive !== false} 
                              onCheckedChange={(checked) => updateAlert(a.id, { isActive: checked })}
                            />
                          </div>
                           <Button variant="outline" size="sm" className="h-8 gap-1 border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground" onClick={() => handleEditAlert(a)}>
                             <Pencil className="w-3.5 h-3.5" /> Modifier
                          </Button>
                       </div>
                     </div>
                    );
                  })}
                </div>
             )}
           </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAlertsPage;
